import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { type FormEvent, useRef, useState } from 'react'
import { BotaoGravarAudio } from '../../components/BotaoGravarAudio'
import { atalhoDeFormatacao, BarraDeFormatacao } from '../../components/Formatacao'
import { FilterSelect } from '../../components/filters'
import { InserirVariavel } from '../../components/InserirVariavel'
import {
  IconBroadcast,
  IconChevronDown,
  IconClose,
  IconDoc,
  IconPaperclip,
  IconPlug,
  IconVideoCircular,
} from '../../components/icons'
import { Modal } from '../../components/Modal'
import { avatarColor, initials } from '../../lib/format'
import { lerIdioma, t, tRico } from '../../lib/i18n'
import { renderizarMarkdown } from '../../lib/markdown'
import { useTRPC } from '../../lib/trpc'
import { conferirVideoCircular } from '../../lib/video-circular'

export const Route = createFileRoute('/_app/transmissao')({ component: Broadcasts })

/** Sob demanda e guardado por idioma — como constante ficaria preso em pt-BR. */
const QUANDO = new Map<string, Intl.DateTimeFormat>()

function quando(valor: Date | string | number): string {
  const idioma = lerIdioma()
  let formatador = QUANDO.get(idioma)
  if (!formatador) {
    formatador = new Intl.DateTimeFormat(idioma, {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
    QUANDO.set(idioma, formatador)
  }
  return formatador.format(new Date(valor))
}

/**
 * Rótulo e cor de cada estado.
 *
 * As cores saem dos tokens de etapa em vez de hexas novos: são o par
 * claro/escuro que o design já validou, e o vocabulário é o mesmo que a pessoa
 * lê na Inbox o dia inteiro — verde é terminou, âmbar é pede atenção, vermelho
 * é não vai acontecer.
 *
 * O rótulo fica em português e é traduzido na hora de desenhar o selo — um
 * `t()` aqui seria resolvido na importação e prenderia a tela no idioma de
 * abertura.
 */
const STATUS: Record<string, { rotulo: string; fundo: string; tinta: string }> = {
  rascunho: { rotulo: 'Rascunho', fundo: 'var(--st-fech-soft)', tinta: 'var(--st-fech)' },
  agendada: { rotulo: 'Agendada', fundo: 'var(--st-aguard-soft)', tinta: 'var(--st-aguard)' },
  enviando: { rotulo: 'Enviando', fundo: 'var(--st-aberto-soft)', tinta: 'var(--st-aberto)' },
  pausada: { rotulo: 'Pausada', fundo: 'var(--st-atend-soft)', tinta: 'var(--st-atend)' },
  concluida: { rotulo: 'Concluída', fundo: 'var(--st-resolv-soft)', tinta: 'var(--st-resolv)' },
  cancelada: { rotulo: 'Cancelada', fundo: 'var(--danger-soft)', tinta: 'var(--danger)' },
}

/** Rótulo do estado, traduzido na hora de desenhar o selo. */
function rotuloDoStatus(status: string): string {
  const entrada = STATUS[status]
  return entrada ? t(entrada.rotulo) : status
}

const ETAPAS = [
  { key: 'aberto', label: 'Aberto' },
  { key: 'em_atendimento', label: 'Em atendimento' },
  { key: 'aguardando', label: 'Aguardando' },
  { key: 'resolvido', label: 'Resolvido' },
  { key: 'fechado', label: 'Fechado' },
]

const PERIODOS = [
  { key: 'hoje', label: 'Hoje' },
  { key: 'ontem', label: 'Ontem' },
  { key: '7d', label: '7 dias' },
  { key: '30d', label: '30 dias' },
  { key: '90d', label: '90 dias' },
]

interface Anexo {
  mediaId: string
  kind: string
  fileName: string
}

function Broadcasts() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const channels = useQuery(trpc.channel.list.queryOptions())
  const tags = useQuery(trpc.lead.tags.queryOptions())
  const members = useQuery(trpc.workspace.members.queryOptions())
  const historico = useQuery(trpc.broadcast.list.queryOptions())

  const [channelId, setChannelId] = useState<string | null>(null)
  const [stageKeys, setStageKeys] = useState<string[]>([])
  const [tagIds, setTagIds] = useState<string[]>([])
  const [excludeTagIds, setExcludeTagIds] = useState<string[]>([])
  const [campaignIds, setCampaignIds] = useState<string[]>([])
  const [assigneeUserIds, setAssigneeUserIds] = useState<string[]>([])
  const [period, setPeriod] = useState<string | null>(null)
  const [waitingOnly, setWaitingOnly] = useState(false)
  const [unreadOnly, setUnreadOnly] = useState(false)
  const [includeTerminal, setIncludeTerminal] = useState(false)

  const [nome, setNome] = useState('')
  const [texto, setTexto] = useState('')
  const [anexos, setAnexos] = useState<Anexo[]>([])
  // Cada botão carrega um id próprio: usar o índice como chave faz o React
  // reaproveitar o campo errado quando um botão do meio é removido, e o texto
  // digitado pula de linha.
  const [botoes, setBotoes] = useState<
    Array<{ id: string; tipo: 'link' | 'resposta'; text: string; url: string }>
  >([])
  const [protegido, setProtegido] = useState(false)
  // Ligado por padrão: quem coloca um botão de link quase sempre quer saber se
  // clicaram. Quem não quer, desmarca.
  const [rastrear, setRastrear] = useState(true)
  const [agendarEm, setAgendarEm] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [subindo, setSubindo] = useState(false)
  const [formAberto, setFormAberto] = useState(false)
  const arquivoRef = useRef<HTMLInputElement>(null)
  const circularRef = useRef<HTMLInputElement>(null)
  /** Aviso do vídeo circular — corte por não ser quadrado, por exemplo. */
  const [avisoCircular, setAvisoCircular] = useState<string | null>(null)
  const textoRef = useRef<HTMLTextAreaElement>(null)

  const campanhas = useQuery({
    ...trpc.broadcast.campaigns.queryOptions({ channelId: channelId ?? '' }),
    enabled: Boolean(channelId),
  })

  const publico = {
    channelId: channelId ?? '',
    ...(stageKeys.length ? { stageKeys } : {}),
    ...(tagIds.length ? { tagIds } : {}),
    ...(excludeTagIds.length ? { excludeTagIds } : {}),
    ...(campaignIds.length ? { campaignIds } : {}),
    ...(assigneeUserIds.length ? { assigneeUserIds } : {}),
    ...(period ? { period: period as 'hoje' } : {}),
    ...(waitingOnly ? { waitingOnly } : {}),
    ...(unreadOnly ? { unreadOnly } : {}),
    includeTerminal,
  }

  // Recontado a cada mudança de filtro: é o número que diz se o recorte faz
  // sentido antes de a mensagem sair para centenas de pessoas.
  const alcance = useQuery({
    ...trpc.broadcast.preview.queryOptions(publico),
    enabled: Boolean(channelId),
  })

  const criar = useMutation({
    ...trpc.broadcast.create.mutationOptions(),
    onSuccess: () => {
      setNome('')
      setTexto('')
      setAnexos([])
      setBotoes([])
      setAgendarEm('')
      setErro(null)
      setFormAberto(false)
      queryClient.invalidateQueries({ queryKey: [['broadcast']] })
    },
    onError: (error) => setErro(error.message),
  })

  const mudarStatus = useMutation({
    ...trpc.broadcast.setStatus.mutationOptions(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [['broadcast']] }),
    onError: (error) => setErro(error.message),
  })

  /** `extras` carrega duração e intenção quando o arquivo veio do gravador. */
  async function subirArquivo(file: File, extras?: { duracao: number; kind: string }) {
    setSubindo(true)
    setErro(null)
    try {
      const form = new FormData()
      form.set('file', file)
      if (extras) {
        form.set('duration', String(extras.duracao))
        form.set('kind', extras.kind)
      }
      const resposta = await fetch('/api/upload', {
        method: 'POST',
        body: form,
        credentials: 'include',
        headers: { 'x-workspace-id': localStorage.getItem('reydelgreen:workspace') ?? '' },
      })
      const resultado = (await resposta.json()) as {
        mediaId?: string
        kind?: string
        fileName?: string
        error?: string
      }

      if (!resposta.ok || !resultado.mediaId) {
        setErro(resultado.error ?? 'Falha ao subir o arquivo')
        return
      }

      setAnexos((atuais) => [
        ...atuais,
        {
          mediaId: resultado.mediaId as string,
          kind: kindDoArquivo(file, resultado.kind),
          fileName: resultado.fileName ?? file.name,
        },
      ])
    } catch {
      setErro('Falha de rede ao subir o arquivo')
    } finally {
      setSubindo(false)
    }
  }

  /**
   * Confere o vídeo antes de subir.
   *
   * A recusa do Telegram só apareceria na fila, com o arquivo já no R2 e a
   * transmissão já disparando — tarde demais para corrigir.
   */
  async function anexarCircular(file: File) {
    setErro(null)
    setAvisoCircular(null)

    const veredito = await conferirVideoCircular(file)
    if ('erro' in veredito) {
      setErro(veredito.erro)
      return
    }

    setAvisoCircular(veredito.aviso)
    await subirArquivo(file, { duracao: 0, kind: 'video_note' })
  }

  /** Vídeo circular vai sozinho: o Telegram não o agrupa em álbum. */
  const temCircular = anexos.some((anexo) => anexo.kind === 'video_note')

  function enviar(event: FormEvent) {
    event.preventDefault()
    if (!channelId) return

    if (!nome.trim()) {
      setErro(t('Dê um nome interno para reconhecer esta transmissão depois.'))
      return
    }

    criar.mutate({
      name: nome.trim(),
      publico: { ...publico, channelId },
      ...(texto.trim() ? { body: texto.trim() } : {}),
      media: anexos.map((anexo) => ({ mediaId: anexo.mediaId, kind: anexo.kind })),
      buttons: botoes
        .filter((botao) => botao.text.trim() && (botao.tipo === 'resposta' || botao.url.trim()))
        .map((botao) =>
          botao.tipo === 'resposta'
            ? { tipo: 'resposta' as const, text: botao.text }
            : { tipo: 'link' as const, text: botao.text, url: botao.url },
        ),
      protectContent: protegido,
      trackLinks: rastrear,
      ...(agendarEm ? { scheduledAt: new Date(agendarEm).getTime() } : {}),
    })
  }

  const contas = (channels.data ?? []).filter((row) => row.isEnabled)
  const album = anexos.length > 1
  const contaEscolhida = contas.find((conta) => conta.id === channelId)

  /**
   * Duas razões impedem botão, e é melhor dizer qual.
   *
   * Conta business é limite do Telegram, não escolha nossa: ele recusa
   * `reply_markup` em mensagem enviada em nome dela. Álbum idem.
   */
  const motivoSemBotao =
    contaEscolhida?.type === 'business'
      ? t(
          'Conta business não aceita botões — é limite do Telegram. Use um bot para transmissão com botão.',
        )
      : album
        ? t('Álbum com várias mídias não aceita botões.')
        : null

  const botoesBloqueados = Boolean(motivoSemBotao)

  return (
    <div className="h-full overflow-y-auto">
      <div className="fim-seguro mx-auto max-w-3xl px-5 pt-4 md:pt-8">
        {/* Título e botão na mesma linha, e a explicação embaixo: com o texto
            longo dentro do flex, ele empurrava o botão para a linha de baixo. */}
        <div className="flex items-center gap-3">
          <h1 className="min-w-0 font-display text-2xl">{t('Transmissão')}</h1>
          <button
            type="button"
            onClick={() => setFormAberto(true)}
            className="ml-auto shrink-0 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-on-accent hover:bg-accent-ink"
          >
            {t('Nova transmissão')}
          </button>
        </div>

        <p className="mt-1 max-w-2xl text-xs leading-relaxed text-ink-2">
          {t(
            'Envia a mesma mensagem para um recorte de conversas, por uma conta conectada. O Telegram só entrega para quem já falou com essa conta — não existe importar lista.',
          )}
        </p>

        {formAberto && (
          <Modal
            title={t('Nova transmissão')}
            description={t(
              'O Telegram só entrega para quem já falou com a conta escolhida — não existe importar lista.',
            )}
            onClose={() => setFormAberto(false)}
            largura="max-w-2xl"
          >
            <form onSubmit={enviar}>
              <div className="border-b border-line px-4 py-3">
                <h3 className="text-sm font-semibold">{t('Quem recebe')}</h3>
                <p className="mt-0.5 text-xs text-ink-2">
                  {t(
                    'Os mesmos filtros da Inbox. O número ao lado é quanta gente o recorte alcança agora.',
                  )}
                </p>
              </div>

              <div className="flex flex-col gap-3 p-4">
                <Campo rotulo={t('Conta que envia')}>
                  <select
                    value={channelId ?? ''}
                    onChange={(event) => {
                      setChannelId(event.target.value || null)
                      setCampaignIds([])
                    }}
                    className="w-full rounded-md border border-line bg-surface-2 px-2 py-2 text-sm"
                  >
                    <option value="">{t('Escolha a conta…')}</option>
                    {contas.map((conta) => (
                      <option key={conta.id} value={conta.id}>
                        {conta.name} · {conta.type === 'bot' ? 'Bot' : 'Conta business'}
                      </option>
                    ))}
                  </select>
                </Campo>

                {channelId && (
                  <>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <FilterSelect
                        label={t('Etapa')}
                        placeholder={t('Qualquer')}
                        multiple
                        options={ETAPAS.map((etapa) => ({
                          value: etapa.key,
                          label: t(etapa.label),
                        }))}
                        values={stageKeys}
                        onChange={setStageKeys}
                      />
                      <FilterSelect
                        label={t('Tags')}
                        placeholder={t('Todas')}
                        multiple
                        options={(tags.data ?? [])
                          .filter((tag) => !excludeTagIds.includes(tag.id))
                          .map((tag) => ({ value: tag.id, label: tag.label, dot: tag.color }))}
                        values={tagIds}
                        onChange={setTagIds}
                      />
                      {/* A mesma tag nos dois lados zera o público sem explicar
                          por quê; cada lado esconde o que o outro já escolheu. */}
                      <FilterSelect
                        label={t('Excluir tags')}
                        placeholder={t('Nenhuma')}
                        multiple
                        options={(tags.data ?? [])
                          .filter((tag) => !tagIds.includes(tag.id))
                          .map((tag) => ({ value: tag.id, label: tag.label, dot: tag.color }))}
                        values={excludeTagIds}
                        onChange={setExcludeTagIds}
                      />
                      <FilterSelect
                        label={t('Campanha')}
                        placeholder={t('Todas')}
                        multiple
                        options={(campanhas.data ?? []).map((campanha) => ({
                          value: campanha.id,
                          label: campanha.label,
                        }))}
                        values={campaignIds}
                        onChange={setCampaignIds}
                      />
                      <FilterSelect
                        label={t('Responsável')}
                        placeholder={t('Qualquer um')}
                        multiple
                        options={(members.data ?? []).map((membro) => ({
                          value: membro.userId,
                          label: membro.name,
                        }))}
                        values={assigneeUserIds}
                        onChange={setAssigneeUserIds}
                      />
                      <FilterSelect
                        label={t('Última mensagem')}
                        placeholder={t('Qualquer data')}
                        options={PERIODOS.map((periodo) => ({
                          value: periodo.key,
                          label: t(periodo.label),
                        }))}
                        values={period ? [period] : []}
                        onChange={([valor]) => setPeriod(valor ?? null)}
                      />
                    </div>

                    <div className="flex flex-wrap gap-3 text-xs text-ink-2">
                      <Marcador ligado={waitingOnly} onChange={setWaitingOnly}>
                        {t('Só quem está sem resposta')}
                      </Marcador>
                      <Marcador ligado={unreadOnly} onChange={setUnreadOnly}>
                        {t('Só com mensagem não lida')}
                      </Marcador>
                      <Marcador ligado={includeTerminal} onChange={setIncludeTerminal}>
                        {t('Incluir conversas fechadas')}
                      </Marcador>
                    </div>

                    <p className="rounded-lg border border-accent-soft-line bg-accent-soft px-3 py-2 text-sm text-accent-ink">
                      {tRico(
                        alcance.data?.total === 1
                          ? '{n} conversa recebe esta transmissão.'
                          : '{n} conversas recebem esta transmissão.',
                        {
                          n: (
                            <strong className="text-md font-bold tabular-nums">
                              {alcance.isPending ? '—' : (alcance.data?.total ?? 0)}
                            </strong>
                          ),
                        },
                      )}
                    </p>

                    {/**
                     * Bloqueados são parte do total acima, não um número
                     * paralelo — eles entram no disparo e falham um a um.
                     *
                     * Não filtramos de propósito: `blocked` é a última verdade
                     * conhecida, e quem desbloqueou sem escrever só é
                     * descoberto tentando. O aviso existe para a conta fechar
                     * quando o relatório mostrar essas falhas — sem ele, N
                     * falhas num disparo parecem defeito do CRM.
                     */}
                    {(alcance.data?.bloqueados ?? 0) > 0 && (
                      <p className="mt-1.5 rounded-lg border border-line bg-surface-2 px-3 py-2 text-2xs text-ink-2">
                        <strong className="font-bold tabular-nums text-danger">
                          {alcance.data?.bloqueados}
                        </strong>{' '}
                        {alcance.data?.bloqueados === 1
                          ? 'dessas pessoas bloqueou'
                          : 'dessas pessoas bloquearam'}{' '}
                        o bot e vai aparecer como falha no relatório. A tentativa continua sendo
                        feita: é assim que o CRM descobre quem desbloqueou.
                      </p>
                    )}
                  </>
                )}
              </div>

              <div className="border-y border-line px-4 py-3"></div>

              <div className="flex flex-col gap-3 p-4">
                <Campo rotulo={t('Nome interno')}>
                  {/* Obrigatório: é por ele que a transmissão é reconhecida na
                  lista depois, e "Transmissão" repetido cinco vezes não
                  distingue nada. Não vai para o cliente. */}
                  <input
                    value={nome}
                    onChange={(event) => setNome(event.target.value)}
                    placeholder={t('Promoção de setembro')}
                    maxLength={80}
                    required
                    className="w-full rounded-md border border-line bg-surface-2 px-2 py-2 text-sm placeholder:text-ink-3"
                  />
                </Campo>

                <Campo rotulo={t('Anexos')}>
                  <input
                    ref={arquivoRef}
                    type="file"
                    className="hidden"
                    onChange={(event) => {
                      const file = event.target.files?.[0]
                      event.target.value = ''
                      if (file) subirArquivo(file)
                    }}
                  />
                  {/* Entrada separada, e só MP4: o vídeo circular não é um
                  anexo qualquer que o servidor possa adivinhar pelo mime — é
                  uma escolha de como a mensagem chega. */}
                  <input
                    ref={circularRef}
                    type="file"
                    accept="video/mp4"
                    className="hidden"
                    onChange={(event) => {
                      const file = event.target.files?.[0]
                      event.target.value = ''
                      if (file) void anexarCircular(file)
                    }}
                  />
                  <span className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      disabled={subindo || anexos.length >= 10 || temCircular}
                      onClick={() => arquivoRef.current?.click()}
                      className="inline-flex items-center gap-2 rounded-md border border-dashed border-line-strong px-3 py-2 text-xs font-semibold text-ink-2 hover:border-ink-3 hover:text-ink disabled:opacity-50"
                    >
                      <IconPaperclip className="size-4" />
                      {subindo ? t('Subindo…') : t('Anexar imagem, vídeo ou arquivo')}
                    </button>

                    <button
                      type="button"
                      disabled={subindo || anexos.length > 0}
                      title={
                        anexos.length > 0
                          ? t(
                              'O vídeo circular vai sozinho — o Telegram não o agrupa com outros anexos',
                            )
                          : t('Vídeo circular (nota de vídeo)')
                      }
                      onClick={() => circularRef.current?.click()}
                      className="inline-flex items-center gap-2 rounded-md border border-dashed border-line-strong px-3 py-2 text-xs font-semibold text-ink-2 hover:border-ink-3 hover:text-ink disabled:opacity-50"
                    >
                      <IconVideoCircular className="size-4" />
                      {t('Vídeo circular')}
                    </button>

                    <BotaoGravarAudio
                      disabled={subindo || anexos.length >= 10}
                      onGravado={(audio) =>
                        void subirArquivo(
                          new File([audio.blob], audio.fileName, { type: audio.blob.type }),
                          { duracao: audio.duracao, kind: audio.kind },
                        )
                      }
                    />
                  </span>

                  {anexos.length > 0 && (
                    <ul className="mt-2 flex flex-col gap-1">
                      {anexos.map((anexo, indice) => (
                        <li
                          key={anexo.mediaId}
                          className="flex items-center gap-2 rounded-md border border-line bg-surface-2 px-2 py-1.5"
                        >
                          {anexo.kind === 'video_note' ? (
                            <IconVideoCircular className="size-3.5 flex-none text-ink-3" />
                          ) : (
                            <IconDoc className="size-3.5 flex-none text-ink-3" />
                          )}
                          <span className="min-w-0 flex-1 truncate text-2xs text-ink-2">
                            {anexo.fileName}
                            {anexo.kind === 'video_note' && (
                              <span className="ml-1 text-ink-3">· vídeo circular</span>
                            )}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              setAnexos((atuais) => atuais.filter((_, i) => i !== indice))
                            }
                            aria-label={`Remover ${anexo.fileName}`}
                            className="inline-flex size-5 flex-none items-center justify-center rounded text-ink-3 hover:bg-surface-3 hover:text-ink"
                          >
                            <IconClose className="size-3" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  {temCircular && (
                    <p className="mt-2 text-2xs text-ink-3">
                      {t(
                        'Vídeo circular não tem legenda no Telegram. O vídeo chega primeiro e o texto abaixo vem em seguida, numa mensagem própria, com os botões.',
                      )}
                    </p>
                  )}

                  {avisoCircular && (
                    <p
                      className="mt-1 text-2xs font-semibold"
                      style={{ color: 'var(--tag-amber)' }}
                    >
                      {avisoCircular}
                    </p>
                  )}

                  {album && (
                    <p className="mt-1.5 text-2xs text-ink-3">
                      {t(
                        'Com mais de um arquivo, tudo vai como álbum numa mensagem só — e o Telegram não aceita botões em álbum.',
                      )}
                    </p>
                  )}
                </Campo>

                <Campo rotulo={t('Texto')}>
                  {/* A faixa fica sempre à vista aqui, ao contrário do chat:
                  esta tela tem espaço e a mensagem é escrita uma vez só, com
                  calma, para centenas de pessoas. */}
                  <div className="flex items-center gap-0.5 rounded-t-md border border-b-0 border-line bg-surface-2 px-1.5 py-1">
                    <BarraDeFormatacao campo={textoRef} valor={texto} aoMudar={setTexto} />
                  </div>
                  <textarea
                    ref={textoRef}
                    value={texto}
                    onChange={(event) => setTexto(event.target.value)}
                    onKeyDown={(evento) => {
                      atalhoDeFormatacao(evento, {
                        campo: textoRef,
                        valor: texto,
                        aoMudar: setTexto,
                      })
                    }}
                    rows={4}
                    maxLength={4000}
                    placeholder={t('Oi {primeiro_nome}! Abrimos as vagas de setembro…')}
                    className="w-full resize-y rounded-b-md border border-line bg-surface-2 px-2 py-2 text-sm placeholder:text-ink-3"
                  />

                  <InserirVariavel campo={textoRef} valor={texto} onInserir={setTexto} />
                </Campo>

                <Campo rotulo={t('Botões')}>
                  {botoes.map((botao, indice) => (
                    <div key={botao.id} className="mb-1.5 flex gap-1.5">
                      <input
                        value={botao.text}
                        onChange={(event) =>
                          setBotoes((atuais) =>
                            atuais.map((item, i) =>
                              i === indice ? { ...item, text: event.target.value } : item,
                            ),
                          )
                        }
                        placeholder={t('Texto do botão')}
                        maxLength={40}
                        className="min-w-0 flex-1 rounded-md border border-line bg-surface-2 px-2 py-1.5 text-xs placeholder:text-ink-3"
                      />
                      {botao.tipo === 'link' ? (
                        <input
                          value={botao.url}
                          onChange={(event) =>
                            setBotoes((atuais) =>
                              atuais.map((item, i) =>
                                i === indice ? { ...item, url: event.target.value } : item,
                              ),
                            )
                          }
                          placeholder="https://…"
                          className="min-w-0 flex-[2] rounded-md border border-line bg-surface-2 px-2 py-1.5 text-xs placeholder:text-ink-3"
                        />
                      ) : (
                        // O de resposta não tem destino: o "destino" é a fila de
                        // quem atende, e o texto do botão vira a mensagem.
                        <span className="flex min-w-0 flex-[2] items-center rounded-md border border-dashed border-line px-2 py-1.5 text-2xs text-ink-3">
                          {t('Chega como mensagem não lida do lead')}
                        </span>
                      )}

                      <button
                        type="button"
                        onClick={() => setBotoes((atuais) => atuais.filter((_, i) => i !== indice))}
                        aria-label={t('Remover botão')}
                        className="inline-flex size-7 flex-none items-center justify-center rounded text-ink-3 hover:bg-surface-3 hover:text-ink"
                      >
                        <IconClose className="size-3" />
                      </button>
                    </div>
                  ))}

                  <span className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      disabled={botoesBloqueados || botoes.length >= 6}
                      onClick={() =>
                        setBotoes((atuais) => [
                          ...atuais,
                          { id: crypto.randomUUID(), tipo: 'link', text: '', url: '' },
                        ])
                      }
                      title={motivoSemBotao ?? undefined}
                      className="rounded-md border border-line bg-surface-2 px-2 py-1 text-2xs font-semibold text-ink-2 hover:border-line-strong hover:bg-surface-3 hover:text-ink disabled:cursor-default disabled:opacity-40"
                    >
                      + {t('botão de link')}
                    </button>

                    <button
                      type="button"
                      disabled={botoesBloqueados || botoes.length >= 6}
                      onClick={() =>
                        setBotoes((atuais) => [
                          ...atuais,
                          { id: crypto.randomUUID(), tipo: 'resposta', text: '', url: '' },
                        ])
                      }
                      title={motivoSemBotao ?? undefined}
                      className="rounded-md border border-line bg-surface-2 px-2 py-1 text-2xs font-semibold text-ink-2 hover:border-line-strong hover:bg-surface-3 hover:text-ink disabled:cursor-default disabled:opacity-40"
                    >
                      + {t('botão de resposta rápida')}
                    </button>
                  </span>

                  {motivoSemBotao && <p className="mt-1.5 text-2xs text-ink-3">{motivoSemBotao}</p>}

                  {botoes.some((botao) => botao.tipo === 'link') && (
                    <div className="mt-2">
                      <Marcador ligado={rastrear} onChange={setRastrear}>
                        {t('Rastrear cliques nos links')}
                      </Marcador>
                      <p className="mt-1 text-2xs text-ink-3">
                        {rastrear
                          ? t(
                              'O botão sai com um link curto do CRM que redireciona ao destino e conta quem clicou.',
                            )
                          : t('O botão sai com o endereço original, sem medir nada.')}
                      </p>
                    </div>
                  )}
                </Campo>

                <div className="flex flex-wrap items-end gap-4">
                  <Campo rotulo={t('Agendar (opcional)')}>
                    <input
                      type="datetime-local"
                      value={agendarEm}
                      onChange={(event) => setAgendarEm(event.target.value)}
                      className="rounded-md border border-line bg-surface-2 px-2 py-2 text-sm"
                    />
                  </Campo>

                  <Marcador ligado={protegido} onChange={setProtegido}>
                    {t('Bloquear encaminhar e salvar')}
                  </Marcador>
                </div>

                {erro && <p className="text-2xs text-danger">{erro}</p>}

                <div className="flex items-center gap-3">
                  <button
                    type="submit"
                    disabled={
                      !channelId ||
                      !nome.trim() ||
                      criar.isPending ||
                      (alcance.data?.total ?? 0) === 0 ||
                      (!texto.trim() && anexos.length === 0)
                    }
                    className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-on-accent hover:bg-accent-ink disabled:opacity-50"
                  >
                    <IconBroadcast className="size-4" />
                    {criar.isPending
                      ? t('Preparando…')
                      : agendarEm
                        ? t('Agendar transmissão')
                        : t('Enviar para {n}', { n: alcance.data?.total ?? 0 })}
                  </button>

                  {/* Sem confirmação extra: a lista de destinatários está na tela e
                  o botão diz o número. Um "tem certeza?" a mais só treina
                  todo mundo a clicar em "sim" sem ler. */}
                  <p className="text-2xs text-ink-3">
                    {agendarEm
                      ? t('Sai no horário escolhido, no fuso do seu aparelho.')
                      : t('Começa a sair agora, em lotes, respeitando o limite do Telegram.')}
                  </p>
                </div>
              </div>
            </form>
          </Modal>
        )}

        <h2 className="mt-8 text-md font-semibold">{t('Transmissões')}</h2>

        {(historico.data ?? []).length === 0 ? (
          <p className="mt-2 rounded-lg border border-dashed border-line-strong px-4 py-8 text-center text-xs text-ink-3">
            {t('Nenhuma transmissão ainda.')}
          </p>
        ) : (
          <ul className="mt-2 flex flex-col gap-2">
            {(historico.data ?? []).map((envio) => {
              const progresso =
                envio.total > 0 ? ((envio.sent + envio.failed) / envio.total) * 100 : 0
              const emAndamento = envio.status === 'enviando' || envio.status === 'pausada'

              return (
                <li key={envio.id} className="rounded-lg border border-line bg-surface p-3">
                  {/* Cabeçalho sempre visível: nome, estado, conta e autor. O
                      que fica escondido é só o conteúdo enviado. */}
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold">{envio.name}</span>
                    <span
                      className="rounded-full px-2 py-0.5 text-2xs font-bold"
                      style={{
                        background: STATUS[envio.status]?.fundo ?? 'var(--surface-2)',
                        color: STATUS[envio.status]?.tinta ?? 'var(--ink-2)',
                      }}
                    >
                      {rotuloDoStatus(envio.status)}
                    </span>

                    {emAndamento && (
                      <span className="ml-auto flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() =>
                            mudarStatus.mutate({
                              broadcastId: envio.id,
                              status: envio.status === 'pausada' ? 'enviando' : 'pausada',
                            })
                          }
                          className="rounded-md border border-line bg-surface-2 px-2 py-1 text-2xs font-semibold text-ink-2 hover:border-line-strong hover:text-ink"
                        >
                          {envio.status === 'pausada' ? 'Retomar' : 'Pausar'}
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            mudarStatus.mutate({ broadcastId: envio.id, status: 'cancelada' })
                          }
                          className="rounded-md border border-danger/30 bg-danger-soft px-2 py-1 text-2xs font-semibold text-danger hover:border-danger/60"
                        >
                          {t('Cancelar')}
                        </button>
                      </span>
                    )}
                  </div>

                  {/* Duas coisas diferentes que antes ficavam lado a lado sem
                      rótulo, e se liam como uma só: por onde a mensagem saiu
                      (a conta do Telegram) e quem mandou sair (a pessoa). O
                      rótulo na frente separa; o avatar deixa claro qual é a
                      pessoa mesmo de relance. */}
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-2xs text-ink-3">
                    <span className="inline-flex items-center gap-1.5">
                      {t('Enviada por')}
                      <span className="inline-flex items-center gap-1 font-semibold text-ink-2">
                        <IconPlug className="size-3 flex-none" />
                        {envio.channelName}
                      </span>
                    </span>

                    {envio.authorName && (
                      <span className="inline-flex items-center gap-1.5">
                        {t('Criada por')}
                        <span className="inline-flex items-center gap-1 font-semibold text-ink-2">
                          <span
                            className="grid size-[15px] flex-none place-items-center rounded-full text-[8px] font-bold text-white"
                            style={{ background: avatarColor(envio.authorName) }}
                          >
                            {initials(envio.authorName)}
                          </span>
                          {envio.authorName}
                        </span>
                      </span>
                    )}
                  </div>

                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-2">
                    <div
                      className="h-full rounded-full bg-accent"
                      style={{ width: `${Math.min(100, progresso)}%` }}
                    />
                  </div>

                  {/* Números à vista: entrega e cliques são a razão de olhar a
                      lista, e estavam escondidos atrás do "abrir". */}
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-2xs text-ink-3">
                    <span>
                      {tRico('{enviadas} de {total} enviadas', {
                        enviadas: <strong className="tabular-nums text-ink">{envio.sent}</strong>,
                        total: envio.total,
                      })}
                      {envio.failed > 0 && (
                        <span className="ml-1.5 text-danger">
                          · {t('{n} falhas', { n: envio.failed })}
                        </span>
                      )}
                    </span>

                    <Ctr broadcastId={envio.id} />

                    {envio.scheduledAt && envio.status === 'agendada' && (
                      <span>{t('sai {quando}', { quando: quando(envio.scheduledAt) })}</span>
                    )}
                    {envio.finishedAt && (
                      <span>{t('terminou {quando}', { quando: quando(envio.finishedAt) })}</span>
                    )}
                  </div>

                  {envio.lastError && (
                    <p className="mt-2 rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-2xs text-danger">
                      {envio.lastError}
                    </p>
                  )}

                  {/* `details` nativo: abrir e fechar é comportamento do
                      navegador, não estado que precisa ser gerenciado — e
                      funciona com teclado sem nada a mais. */}
                  <details className="group mt-2">
                    <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 text-2xs font-semibold text-ink-3 hover:text-ink [&::-webkit-details-marker]:hidden">
                      <IconChevronDown className="size-3 flex-none transition-transform group-open:rotate-180" />
                      {t('Ver o que foi enviado')}
                    </summary>

                    <Previa envio={envio} />
                  </details>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}

/**
 * Taxa de cliques, na linha de números da transmissão.
 *
 * Some quando não há link rastreado: um "0%" ali seria lido como "ninguém
 * clicou", quando a verdade é que não havia o que clicar.
 */
function Ctr({ broadcastId }: { broadcastId: string }) {
  const trpc = useTRPC()
  const cliques = useQuery(trpc.broadcast.clicks.queryOptions({ broadcastId }))

  if (!cliques.data || cliques.data.links.length === 0) return null

  return (
    <span title={`${cliques.data.pessoas} de ${cliques.data.entregues} que receberam`}>
      <strong className="tabular-nums text-accent-ink">{porcentagem(cliques.data.ctr)}</strong>{' '}
      clicaram
    </span>
  )
}

/** Rótulo do anexo; função para não congelar no idioma de abertura. */
function rotuloDoAnexo(kind: string): string | undefined {
  const rotulos: Record<string, string> = {
    document: t('Arquivo'),
    video: t('Vídeo'),
    audio: t('Áudio'),
    voice: t('Áudio'),
    animation: 'GIF',
  }
  return rotulos[kind]
}

/**
 * O que foi enviado, como o cliente viu.
 *
 * As variáveis aparecem cruas de propósito: cada lead recebeu uma versão
 * diferente, e escolher uma delas para mostrar seria inventar um destinatário
 * que não representa os outros.
 */
function Previa({
  envio,
}: {
  envio: {
    id: string
    body: string | null
    media: Array<{ mediaId: string; kind: string }> | null
    buttons: Array<{ tipo?: 'link' | 'resposta'; text: string; url?: string }> | null
    protectContent: boolean
  }
}) {
  const trpc = useTRPC()
  const anexos = envio.media ?? []

  // Só busca quando a pré-visualização abre — `details` fechado não monta o
  // conteúdo, então a consulta nem sai.
  const cliques = useQuery(trpc.broadcast.clicks.queryOptions({ broadcastId: envio.id }))

  return (
    <div className="mt-3 rounded-lg border border-line bg-surface-2 p-3">
      <p className="mb-2 text-2xs font-bold uppercase tracking-wider text-ink-3">
        {t('O que foi enviado')}
      </p>

      {anexos.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {anexos.map((anexo) =>
            anexo.kind === 'photo' ? (
              <img
                key={anexo.mediaId}
                src={`/api/media/${anexo.mediaId}`}
                alt=""
                className="max-h-28 rounded-md border border-line object-contain"
              />
            ) : (
              <span
                key={anexo.mediaId}
                className="inline-flex items-center gap-1.5 rounded-md border border-line bg-surface px-2 py-1 text-2xs text-ink-2"
              >
                <IconDoc className="size-3.5 flex-none text-ink-3" />
                {rotuloDoAnexo(anexo.kind) ?? t('Arquivo')}
              </span>
            ),
          )}
        </div>
      )}

      {envio.body ? (
        <p className="whitespace-pre-wrap break-words text-xs text-ink">
          {renderizarMarkdown(envio.body)}
        </p>
      ) : (
        <p className="text-xs italic text-ink-3">{t('Sem texto — só o anexo.')}</p>
      )}

      {(envio.buttons ?? []).length > 0 && (
        <ul className="mt-2 flex flex-col gap-1">
          {(envio.buttons ?? []).map((botao) => (
            <li
              key={`${botao.text}-${botao.url ?? 'resposta'}`}
              className="flex flex-wrap items-center gap-2 rounded-md border border-line bg-surface px-2 py-1.5"
            >
              <span
                className={`flex-none text-2xs font-semibold ${
                  botao.tipo === 'resposta' ? 'text-ink-2' : 'text-accent-ink'
                }`}
              >
                {botao.text}
              </span>

              {/* O destino à vista: sem ele não dá para conferir se o botão
                  apontava para onde devia. */}
              {botao.url ? (
                <a
                  href={botao.url}
                  target="_blank"
                  rel="noreferrer"
                  className="min-w-0 flex-1 truncate text-2xs text-ink-3 hover:text-ink hover:underline"
                >
                  {botao.url}
                </a>
              ) : (
                <span className="min-w-0 flex-1 text-2xs text-ink-3">
                  {t('Resposta rápida — chega como mensagem não lida do lead')}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {envio.protectContent && (
        <p className="mt-2 text-2xs text-ink-3">
          {t('Enviada sem permitir encaminhar ou salvar.')}
        </p>
      )}

      {/* Detalhe por link. A taxa geral fica na linha de números da
          transmissão, fora daqui — é o que se olha de relance. CTR por pessoa,
          não por clique: quem abre o mesmo link cinco vezes não é cinco
          interessados. */}
      {(cliques.data?.links.length ?? 0) > 0 && (
        <div className="mt-3 border-t border-line pt-2">
          <p className="mb-1.5 text-2xs font-bold uppercase tracking-wider text-ink-3">
            {t('Cliques por link')}
          </p>

          <ul className="flex flex-col gap-1">
            {(cliques.data?.links ?? []).map((link) => (
              <li
                key={link.id}
                className="flex flex-wrap items-center gap-2 rounded-md bg-surface px-2 py-1.5"
              >
                <span className="min-w-0 flex-1 truncate text-2xs text-ink-2" title={link.url}>
                  {link.label || link.url}
                </span>
                <span className="flex-none text-2xs text-ink-3">
                  {link.pessoas === 1
                    ? t('{n} pessoa', { n: link.pessoas })
                    : t('{n} pessoas', { n: link.pessoas })}
                  {link.cliques !== link.pessoas && ` · ${t('{n} cliques', { n: link.cliques })}`}
                </span>
                <span className="flex-none text-2xs font-semibold tabular-nums text-accent-ink">
                  {porcentagem(link.ctr)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

/** Sem casa decimal: a diferença entre 12% e 12,4% não muda decisão nenhuma. */
function porcentagem(valor: number): string {
  return `${Math.round(valor * 100)}%`
}

/**
 * O tipo que a transmissão guarda.
 *
 * O servidor já classifica pelo mime — só o GIF precisa de correção, porque
 * ele vira `animation` no Telegram e o upload o trata como documento.
 */
function kindDoArquivo(file: File, kindDoUpload: string | undefined): string {
  if (file.type === 'image/gif') return 'animation'
  return kindDoUpload ?? 'document'
}

function Campo({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-2xs font-bold uppercase tracking-wider text-ink-3">
        {rotulo}
      </span>
      {children}
    </label>
  )
}

function Marcador({
  ligado,
  onChange,
  children,
}: {
  ligado: boolean
  onChange: (valor: boolean) => void
  children: React.ReactNode
}) {
  return (
    <label className="inline-flex items-center gap-1.5 text-xs text-ink-2">
      <input
        type="checkbox"
        checked={ligado}
        onChange={(event) => onChange(event.target.checked)}
        className="size-3.5 accent-[var(--accent-ink)]"
      />
      {children}
    </label>
  )
}
